import React, { useState, useEffect, useContext } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { injectIntl, useIntl, WrappedComponentProps } from 'react-intl'
import { Layout, notification } from 'antd'
import axios from 'axios'

import { CourseContext } from '../Components/Course/CourseContext'
import { Editor } from '../Components/Editor/Editor'
import { FlexColumn, FlexRow } from '../Components/FlexboxReusables/FlexItems'
import { Course as CourseType } from '../Components/Course/types'
import Sidebar from '../Components/Sidebar'
import { createDefaultCourseFromPdfImages, createDefaultCourse } from '../Components/Editor/misc'
import EditorSettingsRow from '../Components/Editor/SettingsRow/EditorSettingsRow'
import ViewportOptions from '../Components/Course/ViewportOptions'
import CourseName from '../Components/Course/CourseName'
import CourseDescription from '../Components/Course/CourseDescription'
import Questions from '../Components/Editor/Questions'
import { convertPdfPagesToBase64ImageUrls } from '../Components/Course/utils'
import { AuthContext } from '../Components/Auth/AuthContext'
import LoadingSpinner from '../Components/LoadingSpinner'
import { ThemeContext } from '../Theme/ThemeContext'
import CourseAlreadyPublished from '../Components/Course/CourseAlreadyPublished'
import { apiUrl, imageUrl } from '../globals'

interface CourseProps extends WrappedComponentProps {
  id?: string
}

export enum View {
  EDITOR = 'editor',
  QUESTIONS = 'questions'
}

const Course: React.FC<CourseProps> = () => {
  const intl = useIntl()
  const navigate = useNavigate()

  const location = useLocation()
  const { fromPdf, pdfFile, replacePdfForCourseId, cacheBust: bustFromNav } = (location.state ?? {})


  const { id } = useParams<{ id: string }>()
  const courseId = id ? parseInt(id) : null // react router hook 'useParams' needs a string, but we need a number

  const { courseContext, setCourseContext, createCourse } = useContext(CourseContext)
  const { authUser } = useContext(AuthContext)
  const { isDarkMode } = useContext(ThemeContext)

  const defaultCourse = createDefaultCourse(intl, authUser.id)

  const [course, setCourse] = useState<CourseType | null>(null)
  const [editing, setEditing] = useState<Record<string, boolean>>({ name: false, description: false })
  const [selectedView, setSelectedView] = useState<View>(View.EDITOR)
  const [selectedPage, setSelectedPage] = useState<number>(0)
  const [loadingCourse, setLoadingCourse] = useState<boolean>(true)
  const [cacheBust, setCacheBust] = useState<number>(bustFromNav ?? 0)

  const showEditor = selectedView === View.EDITOR && course && !course.pages[selectedPage].previewPictureIsFromPdf
  const showPreviewPictureInsteadOfEditor = selectedView === View.EDITOR && course && course.fromPdf && course.pages[selectedPage].previewPictureIsFromPdf
  const showQuestions = selectedView === View.QUESTIONS && course

  const deleteCourse = (id: number) => {
    // TODO: Delete course from backend
    // TODO: Delete course from context
    console.log('Delete course with id: ', id)
  }

  const parseCourseContent = (c: CourseType) => ({
    ...c,
    pages: c.pages.map(p => ({
      ...p,
      content: typeof p.content === 'string' ? JSON.parse(p.content as unknown as string) : p.content
    }))
  })

  // handle loading an existing course
  useEffect(() => {
    if (courseContext.loading || fromPdf) {
      return
    }

    const existingCourse = courseContext.courses.find((course: CourseType) => course.id === courseId)

    if (!existingCourse) {
      notification.error({
        message: intl.formatMessage({ id: 'notification.error' }),
        description: intl.formatMessage({ id: 'notification.error.courseNotFound' }),
        placement: 'bottomRight',
      })

      navigate('/')
      return
    }

    setCourse(existingCourse)
    setLoadingCourse(false)
  }, [courseContext])

  // handle creating a new course from a PDF
  useEffect(() => {
    if (id !== 'fromPdf' || !fromPdf || !pdfFile) {
      return
    }

    const run = async () => {
      const images = await convertPdfPagesToBase64ImageUrls(pdfFile)

      const replaceId = (location.state && location.state.replacePdfForCourseId) as number | undefined
      if (replaceId) {
        const payload = {
          pages: images.map((img, idx) => ({
            pageNumber: idx + 1,
            previewPictureBase64: img
          }))
        }

        const res = await axios.post(`${apiUrl}/courses/${replaceId}/replace-pdf`, payload)
        const updated = parseCourseContent(res.data as CourseType)

        setCourse(updated)
        setCourseContext({
          ...courseContext,
          courses: courseContext.courses.map(c => c.id === updated.id ? updated : c),
        })
        setLoadingCourse(false)
        setCacheBust(Date.now())

        navigate(`/course/${replaceId}`)
        return
      }

      const courseFromDb = await createCourse(
        createDefaultCourseFromPdfImages(images, intl, authUser.id)
      )

      setCourse(courseFromDb)
      setCourseContext({
        ...courseContext,
        courses: [...courseContext.courses, courseFromDb],
      })
      setLoadingCourse(false)

      const bust = Date.now()
      setCacheBust(bust)
      navigate(`/course/${replacePdfForCourseId}`, { state: { cacheBust: bust } })
    }

    run()
  }, [id, fromPdf, pdfFile])

  return (
    <>
      {loadingCourse ? (
        <FlexRow height='100%' width='100%' justifyContent='center' alignItems='center'>
          <LoadingSpinner />
        </FlexRow>
      ) : course && (
        <Layout>
          <Sidebar
            pages={course.pages}
            selectedPage={selectedPage}
            setSelectedPage={setSelectedPage}
            setSelectedView={setSelectedView}
            loadingCourse={loadingCourse}
            cacheBust={cacheBust}
          />
          <StyledLayout isDarkMode={isDarkMode}>
            {course.isPublished && <CourseAlreadyPublished course={course} />}
            <CourseName course={course} editing={editing} setEditing={setEditing} setCourse={setCourse} />
            <CourseDescription course={course} editing={editing} setEditing={setEditing} setCourse={setCourse} />
            <FlexRow height='3em' width='100%' padding='0 1em 0 0' justifyContent='space-between'>
              <ViewportOptions
                selectedView={selectedView}
                setSelectedView={setSelectedView}
              />
              {selectedView === View.EDITOR && (
                <EditorSettingsColumn alignItems='flex-end'>
                  <EditorSettingsRow
                    course={course}
                    selectedPage={selectedPage}
                    setSelectedPage={setSelectedPage}
                    deleteCourse={deleteCourse}
                  />
                </EditorSettingsColumn>
              )}
            </FlexRow>
            <Container>
              {showEditor &&
                <Editor
                  course={course}
                  displayedPage={selectedPage}
                  defaultCourse={defaultCourse}
                />
              }
              {showPreviewPictureInsteadOfEditor && (
                <FullsizeImage
                  src={`${imageUrl}/${course.id}/${selectedPage + 1}.jpg${cacheBust ? `?v=${cacheBust}` : ''}`}
                  alt='pdf page'
                />
              )}
              {showQuestions &&
                <Questions
                  course={course}
                  setSelectedView={setSelectedView}
                />
              }
            </Container>
          </StyledLayout>
        </Layout>
      )}
    </>
  )
}

export default injectIntl(Course)

const EditorSettingsColumn = styled(FlexColumn)`
  align-items: flex-end;
`

export const EditIconWrapper = styled.div`
  margin-left: 0.5em;
  padding-bottom: 0.3em;
  cursor: pointer;
`

const Container = styled.div`
  width: 100%;
  height: 80%;
`

const FullsizeImage = styled.img`
  max-width: 100%;
`

const StyledLayout = styled(Layout.Content) <{ isDarkMode: boolean }>`
  background-color: ${(props) => props.isDarkMode ? '#1f1f1f' : 'white'};
  color: ${(props) => props.isDarkMode ? 'white' : 'black'};
`
